import type { Command } from "../document/commands.ts";
import { COLLISION_CELLS_PER_TILE, type EntityInstance, type JsonValue, type MapDocument } from "../document/types.ts";
import { reachWindow, thingBox, type ReachWindow, type ThingBox } from "./cell-sources.ts";
import type { MarkerRef } from "./many-props.ts";
import { updateEntityCommand } from "./panel-commands.ts";
import type { EntityTypeDefinition, JsonSchema } from "./schema.ts";

/**
 * Moving placed markers (P1.6-T316): the one command that carries every selected marker by the same offset. The
 * painted cells and path points a marker's fields hold are counted from its anchor, so they ride along when the
 * marker moves; with the painted area left behind they are rewritten to lie on the same cells of the map. Cells
 * that would fall outside the marker's reach window, or off the map, are dropped, which the undo restores whole.
 */

/**
 * The options of a move: whether the painted area goes with the markers, and the entity types whose schemas say
 * which fields are cells or paths.
 */
export interface MarkerMoveOptions {
  readonly withArea: boolean;
  readonly types: readonly EntityTypeDefinition[];
}

/**
 * A planned move: nothing to do; refused because a marker would end off the map; or the command with how many painted
 * cells and path points it drops for ending outside the map or the marker's reach.
 */
export type MarkerMovePlan =
  | { readonly kind: "nothing" }
  | { readonly kind: "refused"; readonly reason: "off-map" }
  | { readonly kind: "move"; readonly command: Command; readonly dropped: number };

/**
 * Whether a marker's point lies on the map.
 *
 * @param map The map.
 * @param x Column in tiles.
 * @param y Row in tiles.
 * @returns True when the point is inside the map.
 */
export function markerOnMap(map: MapDocument, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < map.width && y < map.height;
}

/**
 * Whether a cell of the map's collision grid lies in a reach window.
 *
 * @param window The window.
 * @param x Column in collision cells.
 * @param y Row in collision cells.
 * @returns True when inside.
 */
function inWindow(window: ReachWindow, x: number, y: number): boolean {
  return x >= window.left && x <= window.right && y >= window.top && y <= window.bottom;
}

/**
 * One marker's cell or path pairs after the move, dropping those that end outside the new reach window.
 *
 * @param value The field's value.
 * @param before The marker's box before.
 * @param after The marker's box after.
 * @param window The new reach window.
 * @param withArea Whether the cells keep their place from the marker.
 * @param lost Counts the pairs dropped.
 * @returns The pairs, or the value untouched when it holds none.
 */
function shiftedPairs(value: JsonValue | undefined, before: ThingBox, after: ThingBox, window: ReachWindow, withArea: boolean, lost: { count: number }): JsonValue | undefined {
  if (!Array.isArray(value)) {
    return value;
  }
  const kept: JsonValue[] = [];
  for (const pair of value) {
    if (!Array.isArray(pair) || pair.length !== 2 || !Number.isInteger(pair[0]) || !Number.isInteger(pair[1])) {
      kept.push(pair);
      continue;
    }
    const [c, r] = pair as [number, number];
    const next: [number, number] = withArea ? [c, r] : [before.ax + c - after.ax, after.ay - before.ay + r];
    if (inWindow(window, after.ax + next[0], after.ay - 1 - next[1])) {
      kept.push(next);
    } else {
      lost.count += 1;
    }
  }
  return kept;
}

/**
 * A marker's fields after the move.
 *
 * @param schema Its type's schema.
 * @param fields Its fields.
 * @param before The marker's box before.
 * @param after The marker's box after.
 * @param window The new reach window.
 * @param withArea Whether the cells keep their place from the marker.
 * @param lost Counts the pairs dropped.
 * @returns The fields, the same object when no cells or points changed.
 */
function movedFields(schema: JsonSchema, fields: EntityInstance["fields"], before: ThingBox, after: ThingBox, window: ReachWindow, withArea: boolean, lost: { count: number }): EntityInstance["fields"] {
  let next: { [key: string]: JsonValue } | null = null;
  for (const [name, property] of Object.entries(schema.properties)) {
    const value = fields[name];
    let changed: JsonValue | undefined = value;
    if (property.type === "cells" || property.type === "path") {
      changed = shiftedPairs(value, before, after, window, withArea, lost);
    } else if (property.type === "list" && property.items.type === "cells" && Array.isArray(value)) {
      changed = value.map((row) => shiftedPairs(row, before, after, window, withArea, lost) as JsonValue);
    }
    if (JSON.stringify(changed) !== JSON.stringify(value)) {
      next = { ...(next ?? fields), [name]: changed as JsonValue };
    }
  }
  return next ?? fields;
}

/**
 * Plans moving markers by an offset in tiles as one command, each marker's fields following the option. Markers
 * the map does not hold are left out; a plan is refused whole when any marker would end off the map, so no
 * marker moves alone.
 *
 * @param map The map.
 * @param refs The markers.
 * @param offset The offset in tiles, rounded to whole collision cells.
 * @param options Whether the painted area goes along and the registered entity types.
 * @returns The plan.
 */
export function planMarkerMove(map: MapDocument, refs: readonly MarkerRef[], offset: { readonly dx: number; readonly dy: number }, options: MarkerMoveOptions): MarkerMovePlan {
  const n = COLLISION_CELLS_PER_TILE;
  const dx = Math.round(offset.dx * n) / n;
  const dy = Math.round(offset.dy * n) / n;
  if (dx === 0 && dy === 0) {
    return { kind: "nothing" };
  }
  const commands: Command[] = [];
  const lost = { count: 0 };
  const seen = new Set<string>();
  for (const ref of refs) {
    const key = `${ref.layerId}\u0000${ref.entityId}`;
    const layer = map.layers.find((l) => l.id === ref.layerId);
    const entity = layer?.kind === "entity" ? layer.entities.find((e) => e.id === ref.entityId) : undefined;
    if (entity === undefined || seen.has(key)) {
      continue;
    }
    seen.add(key);
    const x = entity.x + dx;
    const y = entity.y + dy;
    if (!markerOnMap(map, x, y)) {
      return { kind: "refused", reason: "off-map" };
    }
    const schema = options.types.find((t) => t.type === entity.type)?.schema;
    const before = thingBox(entity, undefined, 1);
    const after = thingBox({ x, y }, undefined, 1);
    const fields = schema === undefined ? entity.fields : movedFields(schema, entity.fields, before, after, reachWindow(after, map.width * n, map.height * n), options.withArea, lost);
    commands.push(updateEntityCommand(map, ref.layerId, ref.entityId, { x, y, fields }));
  }
  if (commands.length === 0) {
    return { kind: "nothing" };
  }
  return { kind: "move", command: commands.length === 1 ? (commands[0] as Command) : { kind: "compound", commands }, dropped: lost.count };
}
